import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box, Typography, Paper } from '@mui/material';
import { Lock } from '@mui/icons-material';

/**
 * RoleProtectedRoute Component
 * 
 * Protects routes based on user roles
 * Redirects to appropriate pages based on authentication and role status
 * 
 * @param {Array} allowedRoles - Array of roles that can access this route
 * @param {ReactNode} children - The component to render if access is granted
 * @param {string} fallbackPath - Path to redirect if role access is denied (default: /dashboard)
 */
const RoleProtectedRoute = ({ 
  children, 
  allowedRoles = [], 
  fallbackPath = '/dashboard',
  requireEmailVerification = true
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  // If user is not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user data is not available, redirect to dashboard
  if (!user || !user.role) {
    return <Navigate to={fallbackPath} replace />;
  }

  if (requireEmailVerification) {
    const authProvider = user.authProvider || user.auth_provider || 'local';
    const emailVerified = user.emailVerified || user.email_verified;

    if (authProvider === 'local' && !emailVerified) {
      return <Navigate to="/verify-email" state={{ from: location }} replace />;
    }
  }

  // Check if user's role is in the allowed roles
  const userRole = user.role.toLowerCase();
  const hasAccess = allowedRoles.length === 0 || allowedRoles.some(role => 
    role.toLowerCase() === userRole
  );

  // If user doesn't have access, show access denied page
  if (!hasAccess) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" p={3}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', maxWidth: 500 }}>
          <Lock sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom color="text.primary">
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            You don't have permission to access this page.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your role: <strong>{user.role}</strong>
            <br />
            Required roles: <strong>{allowedRoles.join(', ')}</strong>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <button
              onClick={() => window.history.back()}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Go Back
            </button>
            <button
              onClick={() => window.location.href = fallbackPath}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Go to Dashboard
            </button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Contractor approval gate: a contractor only sees protected app routes
  // once their application is approved. Test accounts bypass the gate.
  // /contractor/apply and /contractor/application-status are wrapped in
  // plain ProtectedRoute (not RoleProtectedRoute), so they are naturally
  // excluded here and will never trigger a redirect loop.
  if (userRole === 'contractor') {
    const approval = user?.approvalStatus || user?.approval_status;
    const isTest = user?.isTest === true || user?.is_test === true;
    if (!isTest && approval !== 'approved') {
      const submitted = !!(user?.applicationSubmittedAt || user?.application_submitted_at);
      return <Navigate to={submitted ? '/contractor/application-status' : '/contractor/apply'} replace />;
    }
  }

  // User has access, render the children
  return children;
};

export default RoleProtectedRoute;
